import { useCallback, useMemo } from "react"
import { AutoExpandCollapse } from "components/ui/collapse/AutoExpandCollapse/AutoExpandCollapse"
import { CompetitorCategoryTable } from "components/pages/PageConfiguration/layout/CompetitorsModelNumbers/layout/CompetitorsCollapse/layout/CompetitorCategoryTable/CompetitorCategoryTable"
import { createCompetitorsTableBuilder } from "components/pages/PageConfiguration/layout/CompetitorsModelNumbers/layout/CompetitorsCollapse/layout/CompetitorCategoryTable/competitorsTable.builder"
import type { MenuProps } from "antd"
import type { TTopLevelCategory } from "components/pages/PageConfiguration/layout/CompetitorsModelNumbers/layout/CompetitorsCollapse/layout/CompetitorCategoryTable/competitorsTable.builder"
import {
	countCompetitorsModelNumbers,
	filterCompetitorsModelNumbers,
} from "components/pages/PageConfiguration/layout/CompetitorsModelNumbers/competitorsSearch.helpers"
import type { TCompetitorsModelNumbers } from "components/pages/PageConfiguration/layout/CompetitorsModelNumbers/competitorsSearch.helpers"
import { useGetCompetitors, useGetCompetitorsModelNumbers } from "./api/competitors/competitors.hook"

type UseCollapseCompetitorsProps = {
	getCompetitorDropdownItems: (competitorId: number, competitorName: string) => MenuProps["items"]
	getStevensModelDropdownItems: (stevensId: number, stevensModelNumber: string) => MenuProps["items"]
	onEditCompetitorModelNumbers: (
		competitorId: number,
		competitorName: string,
		stevensId: number,
		stevensModelNumber: string,
		modelNumbers: string[],
	) => void
	/** Narrows the rows to those matching this text. Empty means no filtering. */
	searchTerm?: string
}

export const useCollapseCompetitors = ({
	getCompetitorDropdownItems,
	getStevensModelDropdownItems,
	onEditCompetitorModelNumbers,
	searchTerm = "",
}: UseCollapseCompetitorsProps) => {
	const { data: competitorsModelNumbers, isPending: isGetCompetitorsModelNumbersPending } =
		useGetCompetitorsModelNumbers()
	const { data: competitors, isPending: isGetCompetitorsPending } = useGetCompetitors()

	const competitorsPending = isGetCompetitorsModelNumbersPending || isGetCompetitorsPending

	// Search filters the DATA, not the rendered rows. The big category is virtualized, so
	// only ~11 rows exist in the DOM and the browser's own Ctrl+F cannot reach the rest.
	const { data: visibleModelNumbers, matches, matchedTopLevelKeys } = useMemo(
		() => filterCompetitorsModelNumbers(competitorsModelNumbers as TCompetitorsModelNumbers | undefined, searchTerm),
		[competitorsModelNumbers, searchTerm],
	)

	const isSearching = Boolean(searchTerm.trim())

	const totalModelNumbers = useMemo(
		() => countCompetitorsModelNumbers(competitorsModelNumbers as TCompetitorsModelNumbers | undefined),
		[competitorsModelNumbers],
	)

	// Memoized so the builder stays referentially stable — CompetitorCategoryTable
	// keys its useMemo on it, and an unstable builder would rebuild every row on
	// each render. The callers' handlers must be stable too (they are useCallback'd
	// in CompetitorsCollapse).
	const buildTable = useMemo(
		() =>
			createCompetitorsTableBuilder({
				competitors,
				getCompetitorDropdownItems,
				getStevensModelDropdownItems,
				onEditCompetitorModelNumbers,
			}),
		[competitors, getCompetitorDropdownItems, getStevensModelDropdownItems, onEditCompetitorModelNumbers],
	)

	// Generate collapse items for top-level categories within a super category
	const generateSubCollapseItems = useCallback(
		(topLevelCategoryData: TTopLevelCategory, superCategory: string) => {
			return Object.entries(topLevelCategoryData).map(([topLevelCategoryName, subCategories]) => {
				return {
					key: `${superCategory}-${topLevelCategoryName}`,
					label: topLevelCategoryName,
					"data-testid": "collapse-sub-items",
					children: (
						<div className="TableConfiguration">
							<CompetitorCategoryTable
								subCategories={subCategories}
								buildTable={buildTable}
								scrollY={600}
								bordered
								virtual
							/>
						</div>
					),
				}
			})
		},
		[buildTable],
	)

	// Generate top-level collapse items for super categories
	const generateCollapseItems = useCallback(() => {
		if (!visibleModelNumbers) return []

		return Object.entries(visibleModelNumbers).map(([superCategory, categoryData]) => {
			// Handle "Without category" case (array of items directly)
			if (Array.isArray(categoryData)) {
				// Create a simple table for "Without category"
				const subCategories = { "Without category": categoryData }

				return {
					key: superCategory,
					label: superCategory,
					"data-testid": "collapse-items",
					// The big one (~2400 rows), which holds effectively all of the cost. The nested
					// category tables gain nothing measurable but render the same way regardless, so
					// the page has one kind of table rather than two.
					//
					// Being div-based means no <tr>, no <td> and no td[colspan] anywhere on this page,
					// and only ~11 rows mounted per table at a time. Anything reading the DOM — the
					// e2e locators especially — has to account for that.
					children: (
						<div style={{ paddingLeft: "40px" }}>
							<CompetitorCategoryTable subCategories={subCategories} buildTable={buildTable} scrollY={600} virtual />
						</div>
					),
				}
			}

			// Handle normal nested structure
			return {
				key: superCategory,
				label: superCategory,
				"data-testid": "collapse-items",
				children: (
					<div style={{ paddingLeft: "40px" }}>
						<AutoExpandCollapse
							items={generateSubCollapseItems(categoryData as TTopLevelCategory, superCategory)}
							isExpanded={isSearching}
							expandKey={searchTerm}
						/>
					</div>
				),
			}
		})
	}, [generateSubCollapseItems, buildTable, visibleModelNumbers, isSearching, searchTerm])

	return { generateCollapseItems, competitorsPending, matches, totalModelNumbers, matchedTopLevelKeys }
}
